import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns/format';
import { saveAs } from 'file-saver';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { downloadPptxDetailsPresentation } from '@features/campaigns/details/services/downloadPptxDetailsPresentation.ts';
import {
  downloadPptxPerformancePresentation,
  downloadXlsxPerformancePresentation,
} from '@features/campaigns/performance/services/downloadPerformancePresentation.ts';
import { QUERY_KEYS } from '@features/campaigns/shared/consts/queryKeys';
import { CampaignDetails } from '@features/campaigns/shared/types/campaign.ts';
import {
  DownloadedFileExtension,
  DownloadedFileExtensions,
  DownloadedFileType,
  DownloadedFileTypes,
} from '@features/campaigns/shared/types/exportFileDownloadType.ts';
import { downloadSMBUsers } from '@features/users/list/services/downloadSMBUsers.ts';
import { CampaignId } from '@shared/types/campaign';
import { getDownloadedFileName } from '@shared/utils/getDownloadedFileName/getDownloadedFileName.ts';

function getCachedCampaignDetails(
  queryClient: QueryClient,
  campaignId: CampaignId | undefined,
): CampaignDetails | undefined {
  if (!campaignId) {
    return undefined;
  }

  const matchingEntries = queryClient.getQueriesData<CampaignDetails>({
    queryKey: [QUERY_KEYS.CAMPAIGN_DETAILS, campaignId],
  });

  return matchingEntries.find(([, data]) => data !== undefined)?.[1];
}

interface ExportFileDownloadProps {
  fileType: DownloadedFileType;
}

export function useExportFileDownload({ fileType }: ExportFileDownloadProps) {
  const [downloadFileExtension, setDownloadFileExtension] = useState<DownloadedFileExtension | ''>('');
  const queryClient = useQueryClient();
  const { campaignId } = useParams();

  const isDownloadFileExtensionXLSX = downloadFileExtension === DownloadedFileExtensions.XLSX;
  const isDownloadFileExtensionPPTX = downloadFileExtension === DownloadedFileExtensions.PPTX;

  const {
    data: fileData,
    isFetching: isLoading,
    isError,
  } = useQuery({
    queryKey: [fileType, downloadFileExtension, campaignId, isDownloadFileExtensionPPTX, isDownloadFileExtensionXLSX],
    queryFn: () => {
      if (!campaignId) {
        if (fileType === DownloadedFileTypes.LIST && isDownloadFileExtensionXLSX) {
          return downloadSMBUsers();
        }

        return null;
      }

      if (fileType === DownloadedFileTypes.DETAILS) {
        return downloadPptxDetailsPresentation(campaignId);
      }

      if (fileType === DownloadedFileTypes.REPORT && isDownloadFileExtensionPPTX) {
        return downloadPptxPerformancePresentation(campaignId);
      }

      if (fileType === DownloadedFileTypes.REPORT && isDownloadFileExtensionXLSX) {
        return downloadXlsxPerformancePresentation(campaignId);
      }

      return null;
    },
    enabled: !!downloadFileExtension,
    gcTime: 0,
  });

  useEffect(() => {
    if (fileData && !!downloadFileExtension) {
      const cachedCampaignData = getCachedCampaignDetails(queryClient, campaignId);

      let fileName;

      if (cachedCampaignData) {
        fileName = getDownloadedFileName({
          fileType: fileType,
          fileExtension: downloadFileExtension,
          campaignStartDate: cachedCampaignData.startDate,
          campaignSequentialId: cachedCampaignData.sequentialId,
          campaignName: cachedCampaignData.name,
        });
      } else if (isDownloadFileExtensionXLSX) {
        fileName = `smb_users_${format(new Date(), 'yyyyMMdd')}.xlsx`;
      } else {
        fileName = 'filename';
      }

      saveAs(fileData, fileName);
      setDownloadFileExtension('');
    }

    if (isError) {
      setDownloadFileExtension('');
    }
  }, [fileData, downloadFileExtension, isError]);

  return {
    isLoading,
    setDownloadFileExtension,
  };
}
